import { CheckCircleOutlined, CloudOutlined, DisconnectOutlined, GithubOutlined, LockOutlined, LogoutOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { Alert, Avatar, Button, message, Tag } from 'antd';
import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useAuth, userInitials } from '../auth/AuthContext';

export const AccountSettingsPage: React.FC = () => {
  const auth = useAuth();
  const history = useHistory();
  const location = useLocation();
  const [disconnecting, setDisconnecting] = useState(false);
  const status = new URLSearchParams(location.search).get('github');
  const user = auth.user;

  const connectGitHub = () => window.location.assign('/auth/github/connect?returnTo=%2Fsettings%2Faccount');
  const disconnectGitHub = async () => {
    setDisconnecting(true);
    try {
      const response = await fetch('/api/auth/github', { method: 'DELETE', credentials: 'include' });
      if (!response.ok) throw new Error('GitHub connection could not be removed.');
      await auth.refresh();
      message.success('GitHub disconnected from this Click-CI user.');
      history.replace('/settings/account');
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'GitHub disconnect failed.');
    } finally {
      setDisconnecting(false);
    }
  };
  const logout = async () => {
    await auth.logout();
    history.replace('/login');
  };

  return (
    <div className="account-settings-page">
      <header className="account-settings-heading">
        <span className="eyebrow">ACCOUNT SETTINGS</span>
        <h1>Identity & connections</h1>
        <p>Your Qlik identity signs you into Click-CI. Connected developer services grant access to external resources; they do not replace your login.</p>
      </header>

      {status === 'connected' && <Alert showIcon type="success" message="GitHub connected" description="The GitHub account is now bound to this Click-CI user." />}
      {status && status !== 'connected' && <Alert showIcon type="warning" message="GitHub connection was not completed" description="Retry the connection or ask the administrator to verify the GitHub OAuth callback." />}

      <section className="identity-settings-card">
        <div className="settings-card-title">
          <span className="settings-icon qlik"><SafetyCertificateOutlined /></span>
          <div><h2>Qlik Cloud identity</h2><p>Primary Click-CI sign-in</p></div>
          <Tag color="green"><CheckCircleOutlined /> Authenticated</Tag>
        </div>
        <div className="identity-profile-row">
          <Avatar size={54} src={user?.pictureUrl}>{userInitials(user?.displayName ?? 'Click CI')}</Avatar>
          <div><strong>{user?.displayName ?? 'Qlik user'}</strong><span>{user?.email ?? 'Email not provided by Qlik'}</span></div>
        </div>
        <dl className="identity-detail-grid">
          <div><dt>Qlik tenant</dt><dd>{user?.qlikTenantId ?? 'Not available'}</dd></div>
          <div><dt>Click-CI user</dt><dd><code>{user?.id ?? 'Not available'}</code></dd></div>
          <div><dt>Session</dt><dd>{auth.mode === 'oauth' ? 'Server-managed · HttpOnly cookie' : 'Demo compatibility mode'}</dd></div>
        </dl>
      </section>

      <section className="identity-settings-card">
        <div className="settings-card-title">
          <span className="settings-icon github"><GithubOutlined /></span>
          <div><h2>GitHub connection</h2><p>Developer identity bound to your Click-CI account</p></div>
          {auth.github ? <Tag color="green"><CheckCircleOutlined /> Connected</Tag> : <Tag>Not connected</Tag>}
        </div>
        {auth.github ? (
          <>
            <div className="identity-profile-row">
              <Avatar size={54} src={auth.github.avatarUrl}><GithubOutlined /></Avatar>
              <div><strong>{auth.github.name || auth.github.login}</strong><span>@{auth.github.login}{auth.github.email ? ` · ${auth.github.email}` : ''}</span></div>
            </div>
            <dl className="identity-detail-grid">
              <div><dt>Granted scopes</dt><dd>{auth.github.scopes.length ? auth.github.scopes.join(', ') : 'Demo compatibility mode'}</dd></div>
              <div><dt>Credential storage</dt><dd><LockOutlined /> Encrypted on the Click-CI server</dd></div>
            </dl>
            <Button danger icon={<DisconnectOutlined />} loading={disconnecting} onClick={() => void disconnectGitHub()}>Disconnect GitHub</Button>
          </>
        ) : (
          <div className="connection-empty-state">
            <GithubOutlined />
            <div><strong>Connect GitHub after Qlik sign-in</strong><p>Connect your GitHub profile and verified email. Repository automation remains server-managed, and this connection cannot be used to sign in.</p></div>
            <Button type="primary" icon={<GithubOutlined />} onClick={connectGitHub}>Connect GitHub</Button>
          </div>
        )}
      </section>

      <section className="executor-boundary-card">
        <CloudOutlined />
        <div><strong>Execution identity remains separate</strong><p>For this demo, Qlik automation and Git push still use server-managed service credentials. Your connected accounts identify the actor and authorize future per-user operations.</p></div>
        <span>{auth.executors?.qlik ?? 'Server managed'}<br />{auth.executors?.git ?? 'Server managed'}</span>
      </section>

      <Button icon={<LogoutOutlined />} onClick={() => void logout()}>Sign out of Click-CI</Button>
    </div>
  );
};
